import React from "react"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import type { BulkRunOutcome } from "../../../config/bulkResultsInsights.config"
import type { IBulkResultsOutcomeStat } from "../../../types/bulkResultsInsights.types"

export const BulkResultsOutcomesComponent: React.FunctionComponent<
    IBulkResultsOutcomesComponentProps
> = props => {
    const visibleOutcomes = props.outcomes.filter(outcome => outcome.count > 0)
    const total = visibleOutcomes.reduce((sum, item) => sum + item.count, 0)

    if (!total) {
        return null
    }

    return (
        <div className="rounded-[16px] bg-brand-white p-[24px] shadow-sm">
            <div className="mb-[16px] flex items-start justify-between gap-[16px]">
                <PrimaryText typography="h5">Run outcomes</PrimaryText>

                <PrimaryText className="opacity-60 text-right">
                    Every patient falls in exactly one outcome · click a segment
                    to filter results.
                </PrimaryText>
            </div>

            <div className="mb-[24px] flex h-[40px] overflow-hidden rounded-full">
                {visibleOutcomes.map(outcome => (
                    <button
                        key={outcome.key}
                        type="button"
                        className="h-full min-w-[6px] border-0 p-0 transition-opacity"
                        style={{
                            flexGrow: outcome.count,
                            backgroundColor: outcome.color,
                            opacity:
                                props.selectedOutcome &&
                                props.selectedOutcome !== outcome.key
                                    ? 0.4
                                    : 1,
                            outline:
                                props.selectedOutcome === outcome.key
                                    ? `2px solid ${NIRVANA_COLORS.brand.primary}`
                                    : "none",
                            outlineOffset: "-2px",
                        }}
                        aria-label={outcome.label}
                        onClick={() => {
                            props.onSelect(outcome.key, outcome.label)
                        }}
                    />
                ))}
            </div>

            <div className="grid grid-cols-2 gap-x-[16px] gap-y-[8px] md:grid-cols-1">
                {visibleOutcomes.map(outcome => {
                    const isSelected = props.selectedOutcome === outcome.key

                    return (
                        <button
                            key={outcome.key}
                            type="button"
                            className={`
                                flex items-start gap-[8px] rounded-[12px] border border-solid p-[8px] text-left
                                ${isSelected ? "border-brand-primary bg-brand-warmLight025" : "border-transparent"}
                            `}
                            onClick={() => {
                                props.onSelect(outcome.key, outcome.label)
                            }}
                        >
                            <span
                                className="mt-[2px] h-[16px] w-[16px] flex-shrink-0 rounded-[4px]"
                                style={{ backgroundColor: outcome.color }}
                            />

                            <span className="min-w-0 flex-1">
                                <PrimaryText>
                                    {outcome.label}{" "}
                                    <span className="opacity-60">
                                        {outcome.count} patient
                                        {outcome.count === 1 ? "" : "s"} (
                                        {outcome.percentLabel})
                                    </span>
                                </PrimaryText>

                                {outcome.isAction ? (
                                    <PrimaryText
                                        className="mt-[4px]"
                                        weight="medium"
                                        color={
                                            NIRVANA_COLORS.brand.vibrantPurple
                                        }
                                    >
                                        {outcome.actionLabel}
                                    </PrimaryText>
                                ) : (
                                    <PrimaryText className="mt-[4px] opacity-60">
                                        {outcome.actionLabel}
                                    </PrimaryText>
                                )}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

interface IBulkResultsOutcomesComponentProps {
    outcomes: IBulkResultsOutcomeStat[]
    selectedOutcome?: BulkRunOutcome
    onSelect: (outcome: BulkRunOutcome, label: string) => void
}
